import { S } from '../app/app_state.js?v=20260817c';
import { AppRuntime } from '../app/app_runtime.js?v=20260706d';
import { normalizeProjectLevels } from '../utils/common_constants.js?v=20260706d';
import { satelliteFloorplanImageUrl } from '../map/satellite_map_service.js?v=20260706d';
import { normalizeOutdoorSunSimulation } from '../three/outdoor_sun_service.js';
import {
  normalizeOutdoorEnvironment,
  outdoorEnvironmentToSunSimulation
} from '../three/outdoor_environment_service.js';
import { normalizeProjectDimensions } from '../services/dimension_service.js?v=20260904f';
import { normalizeProjectAreaMeasurements } from '../services/area_measurement_service.js?v=20260903c';
import { normalizeProjectTextAnnotations } from '../services/text_annotation_service.js?v=20260903c';
import { normalizeProjectPlanLayerVisibility } from '../render/plan_layer_service.js?v=20260814b';
import { normalizeViewWindows } from '../print/print_plan_3d_view.js?v=20260904f';
import { normalizeViewWindows as normalizeViewWindows2D } from '../print/print_plan_2d_views.js?v=20260904f';
import { normalizePrintSettings } from '../print/print_plan_document.mjs';
import { getProjectModelRevision, markProjectModelChanged } from './project_revision_service.js';
import { invalidateRenderSceneContext } from '../render/render_scene_invalidation.mjs';
import { initializeNewProjectColorBinding } from './color_catalog_binding.js';

const projectNormalizers = new Map();
const projectItemSynchronizers = new Map();
const projectLinkSynchronizers = new Map();
const PROJECT_STATE_MODES = Object.freeze({
  HYDRATE: 'hydrate',
  CREATE: 'create',
  UPGRADE: 'upgrade',
  REPAIR: 'repair'
});
const PROJECT_STATE_MODE_VALUES = Object.freeze(Object.values(PROJECT_STATE_MODES));
const DEFAULT_MUTATING_HANDLER_MODES = Object.freeze([
  PROJECT_STATE_MODES.CREATE,
  PROJECT_STATE_MODES.REPAIR
]);
const projectStateContexts = new WeakMap();
let projectItemSyncCache = null;
let projectLinkSyncCache = null;

if (S.project && typeof S.project === 'object' && !Array.isArray(S.project)) {
  // Der beim App-Start erzeugte leere Zustand ist ein Neuprojekt. Jeder spaeter
  // zugewiesene, noch unbekannte Projektcontainer wird dagegen konservativ als
  // geladener historischer Zustand behandelt.
  projectStateContexts.set(S.project, { mode: PROJECT_STATE_MODES.CREATE, source: 'app-initial-state' });
}

function ensureProjectObject() {
  if (!S.project || typeof S.project !== 'object' || Array.isArray(S.project)) {
    S.project = {};
  }
  return S.project;
}

function normalizeCustomerId(value) {
  const numeric = Number(value);
  return Number.isSafeInteger(numeric) && numeric > 0 ? numeric : null;
}

function comparableCustomerName(value) {
  return String(value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
}

function normalizedPersonName(firstName, lastName, fallback = '') {
  const composed = [firstName, lastName]
    .map(value => String(value ?? '').trim())
    .filter(Boolean)
    .join(' ');
  return composed || String(fallback ?? '').trim();
}

function projectCustomerIdValue(project) {
  if (Object.prototype.hasOwnProperty.call(project, 'customerId')) {
    return normalizeCustomerId(project.customerId);
  }
  if (Object.prototype.hasOwnProperty.call(project, 'customer_id')) {
    return normalizeCustomerId(project.customer_id);
  }
  return normalizeCustomerId(project.kunde_id);
}

function normalizeProjectCustomerIdentity(project = ensureProjectObject()) {
  const customerName = String(
    project.customerName ?? project.customer ?? project.kunde ?? ''
  ).trim();
  const customerId = projectCustomerIdValue(project);
  const identityName = String(project.customerIdentityName ?? '').trim();
  project.customerName = customerName;
  project.customerId = customerId;
  project.customerIdentityName = customerId ? (identityName || customerName) : '';
  const customerContactId = customerId
    ? normalizeCustomerId(
        project.customerContactId
        ?? project.customer_contact_id
        ?? project.kundeKontaktId
        ?? project.kunde_kontakt_id
      )
    : null;
  const customerContactFirstName = customerContactId
    ? String(project.customerContactFirstName ?? project.customer_contact_first_name ?? '').trim()
    : '';
  const customerContactLastName = customerContactId
    ? String(project.customerContactLastName ?? project.customer_contact_last_name ?? '').trim()
    : '';
  const legacyContactName = customerContactId
    ? String(project.customerContactName ?? project.customer_contact_name ?? '').trim()
    : '';
  project.customerContactId = customerContactId;
  project.customerContactFirstName = customerContactFirstName;
  project.customerContactLastName = customerContactLastName;
  project.customerContactName = customerContactId
    ? normalizedPersonName(customerContactFirstName, customerContactLastName, legacyContactName)
    : '';
  return {
    customerId: project.customerId,
    customerName: project.customerName,
    customerIdentityName: project.customerIdentityName
  };
}

function projectCustomerIdForName(project, customerName) {
  if (!project || typeof project !== 'object' || Array.isArray(project)) return null;
  const customerId = projectCustomerIdValue(project);
  if (!customerId) return null;
  const identityName = project.customerIdentityName ?? project.customerName ?? project.customer ?? project.kunde;
  const expected = comparableCustomerName(identityName);
  const actual = comparableCustomerName(customerName);
  return expected && actual === expected ? customerId : null;
}

function normalizedProjectStateMode(value) {
  const mode = String(value || '').trim().toLowerCase();
  if (!PROJECT_STATE_MODE_VALUES.includes(mode)) {
    throw new TypeError(`Unbekannter Projektzustandsmodus: ${value || '(fehlt)'}`);
  }
  return mode;
}

function normalizedProjectStateContext(options) {
  if (!options || typeof options !== 'object' || Array.isArray(options)) {
    throw new TypeError('normalizeProjectState() benötigt einen expliziten Modus.');
  }
  const mode = normalizedProjectStateMode(options.mode);
  const context = { ...options, mode };
  if (mode === PROJECT_STATE_MODES.UPGRADE) {
    const target = options.target;
    const drawingSheetId = String(target?.drawingSheetId || '').trim();
    const systemId = String(target?.systemId || '').trim();
    const groupInstanceId = String(target?.groupInstanceId || '').trim();
    if (!drawingSheetId || !systemId || !groupInstanceId) {
      throw new TypeError('upgrade benötigt drawingSheetId, systemId und groupInstanceId.');
    }
    context.target = { ...target, drawingSheetId, systemId, groupInstanceId };
  }
  if (mode === PROJECT_STATE_MODES.REPAIR && !String(options.reason || '').trim()) {
    throw new TypeError('repair benötigt einen dokumentierten Grund.');
  }
  return context;
}

function projectStateContext(project = S.project) {
  if (!project || typeof project !== 'object' || Array.isArray(project)) {
    return { mode: PROJECT_STATE_MODES.HYDRATE, source: 'unknown-project' };
  }
  const context = projectStateContexts.get(project)
    || { mode: PROJECT_STATE_MODES.HYDRATE, source: 'unclassified-project' };
  return { ...context };
}

function projectStateMode(project = S.project) {
  return projectStateContext(project).mode;
}

// Detached host preparations retain the source mode without running any
// normalizer or resetting the live document's revision-bound sync caches.
function copyProjectStateContext(source, target) {
  const context = projectStateContext(source);
  projectStateContexts.set(target, { ...context, ...(context.target ? { target: { ...context.target } } : {}) });
}

function stableProjectStateContext(project) {
  const context = projectStateContext(project);
  if (context.mode === PROJECT_STATE_MODES.CREATE || context.mode === PROJECT_STATE_MODES.HYDRATE) {
    return context;
  }
  return { mode: PROJECT_STATE_MODES.HYDRATE, source: 'one-shot-context-fallback' };
}

function setProjectStateContext(project, context) {
  const storedContext = { ...context };
  delete storedContext.project;
  projectStateContexts.set(project, storedContext);
  projectItemSyncCache = null;
  projectLinkSyncCache = null;
}

function normalizeProjectState(options) {
  const context = normalizedProjectStateContext(options);
  const project = ensureProjectObject();
  const previousStableContext = stableProjectStateContext(project);
  const oneShot = context.mode === PROJECT_STATE_MODES.UPGRADE
    || context.mode === PROJECT_STATE_MODES.REPAIR;
  setProjectStateContext(project, context);
  try {
    if (context.mode === PROJECT_STATE_MODES.CREATE || context.mode === PROJECT_STATE_MODES.REPAIR) {
      normalizeBaseProjectState();
      // Nur ein neuer Auftrag erhält den aktuellen Katalogstand; Repair und
      // Hydration lassen fehlende Bindungen beim Erstbestand.
      if (context.mode === PROJECT_STATE_MODES.CREATE) initializeNewProjectColorBinding(project);
    } else {
      hydrateBaseProjectState();
    }
    normalizeRegisteredProjectSystems({ ...context, project });
  } finally {
    // Upgrade und Repair sind bewusst einzelne, explizite Operationen. Nach
    // ihrem Abschluss gilt fuer alle passiven Lese-/Syncpfade wieder der zuvor
    // stabile create-/hydrate-Kontext; sonst koennte ein spaeteres Rendering
    // dieselbe Migration unbemerkt erneut ausfuehren.
    if (oneShot) setProjectStateContext(project, previousStableContext);
  }
  return project;
}

function resetTransientProjectRuntimeState() {
  invalidateRenderSceneContext('project');
  // Die registrierten Controller muessen ihre produktbezogenen Vorschauen und
  // Werkzeuge noch gegen das alte Projekt beenden koennen. Deshalb wird dieser
  // Helper unmittelbar VOR dem Austausch von S.project aufgerufen.
  AppRuntime.resetTransientInteractionState();
  AppRuntime.clearGlobalSelection();
  S.selection = {
    items: [],
    groupId: null,
    unitId: null,
    unitIds: [],
    obstacleId: null,
    obstacleIds: [],
    objectAssetId: null,
    objectAssetIds: [],
    dimensionId: null,
    areaMeasurementId: null,
    textAnnotationId: null,
    sketchWall: { nodeId: null, wallId: null, angleId: null, openingId: null },
    sketchWallIds: []
  };
  S.floorplanMeasure = { active: false, referenceMm: '', points: [] };
  S.drag = {
    active: false,
    type: null,
    groupId: null,
    startMouse: null,
    startGroupPos: null,
    panStart: null,
    camStart: null,
    snapCandidate: null
  };
}

function syncProjectItems(options = {}) {
  const project = ensureProjectObject();
  const context = synchronizerContext(options, project);
  const revision = getProjectModelRevision();
  if (options.force !== true
    && projectItemSyncCache?.project === project
    && projectItemSyncCache.revision === revision
    && projectItemSyncCache.mode === context.mode
    && projectItemSyncCache.targetKey === synchronizationTargetKey(context.target)) return project.items || [];
  for (const entry of orderedEntries(projectItemSynchronizers)) {
    if (!handlerSupportsContext(entry, context)) continue;
    entry.handler({ ...context, project });
  }
  projectItemSyncCache = {
    project,
    revision,
    mode: context.mode,
    targetKey: synchronizationTargetKey(context.target)
  };
  return project.items || [];
}

function syncProjectLinks(options = {}) {
  const project = ensureProjectObject();
  const context = synchronizerContext(options, project);
  const revision = getProjectModelRevision();
  if (options.force !== true
    && projectLinkSyncCache?.project === project
    && projectLinkSyncCache.revision === revision
    && projectLinkSyncCache.mode === context.mode
    && projectLinkSyncCache.targetKey === synchronizationTargetKey(context.target)) return projectLinkSyncCache.result;
  let result = null;
  for (const entry of orderedEntries(projectLinkSynchronizers)) {
    if (!handlerSupportsContext(entry, context)) continue;
    result = entry.handler({ ...context, project });
    if (entry.ownerScoped && result === true) markProjectModelChanged(`${entry.id}:links-synchronized`);
  }
  projectLinkSyncCache = {
    project,
    revision: getProjectModelRevision(),
    mode: context.mode,
    targetKey: synchronizationTargetKey(context.target),
    result
  };
  return result;
}

function synchronizerContext(options, project) {
  const source = options && typeof options === 'object' && !Array.isArray(options) ? options : {};
  if (Object.prototype.hasOwnProperty.call(source, 'mode')) {
    return { ...normalizedProjectStateContext(source), project };
  }
  const stored = projectStateContext(project);
  return { ...stored, ...source, mode: stored.mode, project };
}

function synchronizationTargetKey(target) {
  if (!target || typeof target !== 'object') return '';
  return [target.drawingSheetId, target.systemId, target.groupInstanceId]
    .map(value => String(value || '').trim())
    .join('|');
}

function normalizeBaseProjectState() {
  const project = ensureProjectObject();
  ensureSelectionObject();
  if (!Array.isArray(project.objectAssets)) project.objectAssets = [];
  if (!Array.isArray(project.items)) project.items = [];
  if (typeof project.projectNumber !== 'string') project.projectNumber = '';
  normalizeProjectCustomerIdentity(project);
  if (typeof project.orderNumber !== 'string') project.orderNumber = '';
  if (typeof project.seller !== 'string') project.seller = '';
  if (project.sellerUserId === undefined) project.sellerUserId = null;
  if (typeof project.avor !== 'string') project.avor = '';
  if (project.avorUserId === undefined) project.avorUserId = null;
  if (!project.bomOverrides || typeof project.bomOverrides !== 'object' || Array.isArray(project.bomOverrides)) {
    project.bomOverrides = {};
  }
  if (!Array.isArray(project.bomOverrides.manualItems)) project.bomOverrides.manualItems = [];
  if (!Array.isArray(project.bomOverrides.order)) project.bomOverrides.order = [];
  project.printSettings = normalizePrintSettings(project.printSettings, {
    // Zwei getrennte Normalizer (3D-/2D-Fenster unterscheiden sich per `type`,
    // siehe print_plan_3d_view.js/print_plan_2d_views.js) — beide filtern
    // beim Normalisieren jeweils NUR ihren eigenen Typ aus dem gemeinsamen
    // Array heraus. Der Druckansichten-Normalizer wendet diese Kombination auf
    // JEDE gespeicherte Druckansicht an; sonst würde beim Projekt-Laden der
    // jeweils andere Fenstertyp stillschweigend verworfen.
    normalizeViewWindows: viewWindows => [
      ...normalizeViewWindows(viewWindows),
      ...normalizeViewWindows2D(viewWindows)
    ]
  });
  if (![10, 50, 70, 100, 600, 750].includes(Number(project.gridSize))) project.gridSize = 10;
  if (![10, 50, 70, 100, 600, 750].includes(Number(project.helperGridSize))) project.helperGridSize = 600;
  project.showHelperGrid = Boolean(project.showHelperGrid);
  project.currentView = 'plan';
  normalizeProjectPlanLayerVisibility(project);
  project.levels = normalizeProjectLevels(project.levels);
  project.floorplan = normalizeProjectFloorplan(project.floorplan);
  normalizeProjectDimensions(project);
  normalizeProjectAreaMeasurements(project);
  normalizeProjectTextAnnotations(project);
  project.outdoorEnvironment = normalizeOutdoorEnvironment(project.outdoorEnvironment, project.floorplan, project.outdoorSunSimulation);
  project.outdoorSunSimulation = normalizeOutdoorSunSimulation(project.outdoorSunSimulation, project.floorplan);
  project.outdoorSunSimulation = {
    ...project.outdoorSunSimulation,
    ...outdoorEnvironmentToSunSimulation(project.outdoorEnvironment)
  };
  return project;
}

function hydrateBaseProjectState() {
  // Hydration darf keine gespeicherten Fachwerte normalisieren. Insbesondere
  // Druckansichten, Raster, Ebenen, Grundriss, Bemassungen, Flaechen, Texte und
  // Sonnenstand bleiben exakt in der gespeicherten Form. Nur der fluechtige
  // Auswahlcontainer wird fuer die Laufzeit bereitgestellt.
  ensureProjectObject();
  ensureSelectionObject();
  return S.project;
}

function ensureSelectionObject() {
  if (!S.selection || typeof S.selection !== 'object' || Array.isArray(S.selection)) {
    S.selection = {};
  }
  return S.selection;
}

function registerOrderedHandler(registry, id, handler, options = {}) {
  if (!id || typeof handler !== 'function') return false;
  // Registrierungen sind Owner-Claims, keine stillen Updates. Der Plugin-Host
  // darf einen erfolgreichen Rueckgabewert deshalb eindeutig als neu
  // angelegten und bei Bedarf rueckrollbaren Beitrag behandeln.
  if (registry.has(id)) return false;
  const requestedModes = Array.isArray(options.modes) ? options.modes : DEFAULT_MUTATING_HANDLER_MODES;
  const modes = [...new Set(requestedModes.map(normalizedProjectStateMode))];
  registry.set(id, {
    id,
    handler,
    priority: Number.isFinite(Number(options.priority)) ? Number(options.priority) : 1000,
    modes,
    ownerScoped: options.ownerScoped === true
  });
  return true;
}

function orderedEntries(registry) {
  return Array.from(registry.values())
    .sort((a, b) => a.priority - b.priority || String(a.id).localeCompare(String(b.id)));
}

function handlerSupportsContext(entry, context) {
  if (!entry?.modes?.includes(context.mode)) return false;
  if (context.mode !== PROJECT_STATE_MODES.UPGRADE) return true;
  return String(entry.id) === String(context.target?.systemId || '');
}

function registerProjectItemSynchronizer(id, synchronizer, options = {}) {
  const registered = registerOrderedHandler(projectItemSynchronizers, id, synchronizer, options);
  if (registered) projectItemSyncCache = null;
  return registered;
}

function unregisterProjectItemSynchronizer(id) {
  const removed = projectItemSynchronizers.delete(id);
  if (removed) projectItemSyncCache = null;
  return removed;
}

function registerProjectLinkSynchronizer(id, synchronizer, options = {}) {
  const registered = registerOrderedHandler(projectLinkSynchronizers, id, synchronizer, options);
  if (registered) projectLinkSyncCache = null;
  return registered;
}

function unregisterProjectLinkSynchronizer(id) {
  const removed = projectLinkSynchronizers.delete(id);
  if (removed) projectLinkSyncCache = null;
  return removed;
}

function normalizeProjectFloorplan(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  const width = Number(data.width ?? data.realWidthMm);
  const height = Number(data.height ?? data.realHeightMm);
  if (!Number.isFinite(width) || width <= 0 || !Number.isFinite(height) || height <= 0) return null;
  const x = Number(data.x);
  const z = Number(data.z);
  const opacity = Number(data.opacity);
  const kind = String(data.kind || data.backgroundType || '').trim().toLowerCase();
  const provider = String(data.provider || '').trim().toLowerCase();
  const isSatellite = kind === 'satellite' || provider === 'esri' || provider === 'swisstopo';
  const normalized = {
    ...data,
    kind: isSatellite ? 'satellite' : (data.kind || 'image'),
    backgroundType: isSatellite ? 'satellite' : (data.backgroundType || 'floorplan'),
    x: Number.isFinite(x) ? x : -width / 2,
    z: Number.isFinite(z) ? z : -height / 2,
    width,
    height,
    opacity: Number.isFinite(opacity) ? Math.max(0, Math.min(1, opacity)) : 0.5,
    visible: data.visible !== false,
    locked: isSatellite ? true : data.locked !== false
  };
  if (!isSatellite) return normalized;

  normalized.provider = provider === 'esri' ? 'esri' : 'swisstopo';
  normalized.realWidthMm = Math.max(1, Math.round(Number(data.realWidthMm) || width));
  normalized.realHeightMm = Math.max(1, Math.round(Number(data.realHeightMm) || height));
  normalized.imageWidthPx = Math.max(1, Math.round(Number(data.imageWidthPx) || 1200));
  normalized.imageHeightPx = Math.max(1, Math.round(Number(data.imageHeightPx) || 800));
  normalized.width = normalized.realWidthMm;
  normalized.height = normalized.realHeightMm;
  normalized.url = data.url || satelliteFloorplanImageUrl(normalized);
  return normalized;
}

function markProjectFloorplanChanged(reason = 'floorplan-changed') {
  // Floorplans deliberately stay outside Undo, but their committed geometry
  // still invalidates derived data and participates in normal dirty tracking.
  if (!S.file) S.file = { name: '', dirty: true };
  S.file.dirty = true;
  markProjectModelChanged(reason);
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function'
    || typeof CustomEvent !== 'function') return;
  window.dispatchEvent(new CustomEvent('ks:project-dirty', { detail: { label: reason } }));
}

function setProjectFloorplan(data) {
  const normalized = normalizeProjectFloorplan(data);
  const project = ensureProjectObject();
  const previousFloorplan = project.floorplan;
  project.floorplan = normalized;
  project.outdoorEnvironment = normalizeOutdoorEnvironment(project.outdoorEnvironment, normalized, project.outdoorSunSimulation);
  project.outdoorSunSimulation = normalizeOutdoorSunSimulation(project.outdoorSunSimulation, normalized);
  project.outdoorSunSimulation = {
    ...project.outdoorSunSimulation,
    ...outdoorEnvironmentToSunSimulation(project.outdoorEnvironment)
  };
  if (normalized || previousFloorplan) markProjectFloorplanChanged('floorplan-set');
  return normalized;
}

function clearProjectFloorplan() {
  const project = ensureProjectObject();
  const previousFloorplan = project.floorplan;
  project.floorplan = null;
  project.outdoorEnvironment = normalizeOutdoorEnvironment(project.outdoorEnvironment, null, project.outdoorSunSimulation);
  project.outdoorSunSimulation = normalizeOutdoorSunSimulation(project.outdoorSunSimulation, null);
  project.outdoorSunSimulation = {
    ...project.outdoorSunSimulation,
    ...outdoorEnvironmentToSunSimulation(project.outdoorEnvironment)
  };
  if (previousFloorplan) markProjectFloorplanChanged('floorplan-clear');
  return null;
}

function registerProjectNormalizer(id, normalizer, options = {}) {
  return registerOrderedHandler(projectNormalizers, id, normalizer, options);
}

function unregisterProjectNormalizer(id) {
  return projectNormalizers.delete(id);
}

function normalizeRegisteredProjectSystems(context = {}) {
  const project = ensureProjectObject();
  const mode = normalizedProjectStateMode(context.mode);
  const safeContext = { ...context, mode, project };
  for (const entry of orderedEntries(projectNormalizers)) {
    if (!handlerSupportsContext(entry, safeContext)) continue;
    entry.handler(safeContext);
  }
  return project;
}

export {
  PROJECT_STATE_MODES,
  hydrateBaseProjectState,
  normalizeProjectState,
  syncProjectItems,
  syncProjectLinks,
  setProjectFloorplan,
  clearProjectFloorplan,
  markProjectFloorplanChanged,
  normalizeProjectFloorplan,
  registerProjectNormalizer,
  unregisterProjectNormalizer,
  normalizeRegisteredProjectSystems,
  normalizeBaseProjectState,
  projectStateContext,
  projectStateMode,
  copyProjectStateContext,
  resetTransientProjectRuntimeState,
  normalizeProjectCustomerIdentity,
  projectCustomerIdForName,
  registerProjectItemSynchronizer,
  unregisterProjectItemSynchronizer,
  registerProjectLinkSynchronizer,
  unregisterProjectLinkSynchronizer
};
